import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

type AuthState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; token: string; host: string; orgId: number }
  | { status: 'error'; message: string };

/**
 * Fetches a ThoughtSpot embed token via analytics-service and tracks its expiry.
 * Re-fetches automatically 30 seconds before the token expires.
 */
export function useThoughtSpotAuth(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: Record<string, any> | null,
  organizationId: string,
  groupName: string
): AuthState {
  const [state, setState] = useState<AuthState>({ status: 'idle' });
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchToken = useCallback(async () => {
    if (!client?.analyticsService) return;

    setState({ status: 'loading' });

    try {
      const response = await client.analyticsService.getThoughtSpotToken(organizationId, groupName);

      if (response.status !== 'success' || !response.data || !response.metadata?.thoughtspotHost) {
        const message = response.error?.message ?? 'Token request failed';
        Sentry.logger.error(
          Sentry.logger.fmt`ThoughtSpot token error: ${message} (code: ${response.error?.code})`,
          { response }
        );
        setState({ status: 'error', message });
        return;
      }

      const { token, validityTimeInSec, orgId } = response.data;
      const host = response.metadata.thoughtspotHost;

      setState({ status: 'ready', token, host, orgId: parseInt(orgId, 10) });

      // Schedule a refresh 30s before expiry
      const refreshInMs = Math.max((validityTimeInSec - 30) * 1000, 0);
      refreshTimerRef.current = setTimeout(fetchToken, refreshInMs);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      Sentry.logger.error(Sentry.logger.fmt`ThoughtSpot token fetch threw: ${message}`, { err });
      setState({ status: 'error', message });
    }
  }, [client, organizationId, groupName]);

  useEffect(() => {
    fetchToken();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [fetchToken]);

  return state;
}
